import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  AdminRelatedLinks,
  AdminBlogShell,
  AdminListPagination,
  AdminListToolbar,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import { LoadingSpinner } from '../../../shared/ui';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import { fetchYowanAdminOrganizerList } from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');

const ORGANIZER_PAGE_SIZE = 30;
const organizerSortOptions = [
  { value: 'active-status', label: '稼働状態順' },
  { value: 'public-status', label: '公開状態順' },
  { value: 'type-asc', label: '種別昇順' },
  { value: 'type-desc', label: '種別降順' },
  { value: 'name-asc', label: '主催者名昇順' },
  { value: 'name-desc', label: '主催者名降順' },
  { value: 'updated-desc', label: '更新日が新しい順' },
];

const organizerColumnStyle = {
  width: '18rem',
  minWidth: '18rem',
};

const memberColumnStyle = {
  width: '15rem',
  minWidth: '15rem',
};

const statusColumnStyle = {
  width: '7rem',
  minWidth: '7rem',
};

const typeColumnStyle = {
  width: '8rem',
  minWidth: '8rem',
  whiteSpace: 'nowrap',
};

const updatedAtColumnStyle = {
  width: '11.5rem',
  minWidth: '11.5rem',
  whiteSpace: 'nowrap',
};

const actionColumnStyle = {
  width: '6rem',
  minWidth: '6rem',
};

const compareText = (left = '', right = '') => String(left || '').localeCompare(String(right || ''), 'ja');

const comparableDateTime = (value = '') => {
  const timestamp = Date.parse(String(value || '').replace(' ', 'T'));
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const sortOrganizerRows = (rows, sortKey) => {
  const nextRows = [...rows];

  nextRows.sort((left, right) => {
    if (sortKey === 'active-status') {
      if (left.isActive === right.isActive) {
        return compareText(left.organizerName, right.organizerName);
      }
      return left.isActive ? -1 : 1;
    }

    if (sortKey === 'public-status') {
      if (left.publicProfileEnabled === right.publicProfileEnabled) {
        return compareText(left.organizerName, right.organizerName);
      }
      return left.publicProfileEnabled ? -1 : 1;
    }

    if (sortKey === 'type-asc') {
      if (left.organizerType === right.organizerType) {
        return compareText(left.organizerName, right.organizerName);
      }
      return compareText(left.organizerType, right.organizerType);
    }

    if (sortKey === 'type-desc') {
      if (left.organizerType === right.organizerType) {
        return compareText(left.organizerName, right.organizerName);
      }
      return compareText(right.organizerType, left.organizerType);
    }

    if (sortKey === 'name-desc') {
      return compareText(right.organizerName, left.organizerName);
    }

    if (sortKey === 'updated-desc') {
      return comparableDateTime(right.updatedAt) - comparableDateTime(left.updatedAt);
    }

    return compareText(left.organizerName, right.organizerName);
  });

  return nextRows;
};

const statusToUi = (isEnabled) => ({
  label: isEnabled ? '稼働中' : '停止中',
  tone: isEnabled ? 'published' : 'draft',
});

const publicStatusToUi = (isEnabled) => ({
  label: isEnabled ? '公開' : '非公開',
  tone: isEnabled ? 'published' : 'draft',
});

const AdminOrganizersListPage = () => {
  const content = yowanPageContent['/admin/events/organizers'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortKey, setSortKey] = useState('active-status');
  const [currentPage, setCurrentPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [apiCount, setApiCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!content) {
      return undefined;
    }

    let isMounted = true;

    const loadOrganizers = async () => {
      setIsLoading(true);
      setLoadError('');

      try {
        const payload = await fetchYowanAdminOrganizerList({
          page: currentPage,
          pageSize: ORGANIZER_PAGE_SIZE,
          q: searchQuery.trim(),
        });

        if (!isMounted) {
          return;
        }

        setRows(payload.results);
        setApiCount(payload.count);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setRows([]);
        setApiCount(0);
        setLoadError('主催者情報を読み込めませんでした。');
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadOrganizers();

    return () => {
      isMounted = false;
    };
  }, [content, currentPage, searchQuery]);

  if (!content) {
    return null;
  }

  const sortedRows = sortOrganizerRows(rows, sortKey);
  const totalPages = Math.max(1, Math.ceil(apiCount / ORGANIZER_PAGE_SIZE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * ORGANIZER_PAGE_SIZE;
  const visibleRows = sortedRows;

  return (
    <AdminBlogShell activeNavHref="/admin/events/organizers" routeKey="/admin/events/organizers">
      <AdminPageHeader
        title={content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link
            key="primary-action"
            className="c-form__submit"
            to={appendYowanPreviewQueryIfNeeded(content.primaryAction.href, preservedDomainSearch)}
          >
            {content.primaryAction.label}
          </Link>,
        ]}
      />

      <AdminListToolbar
        searchValue={searchQuery}
        onSearchChange={(event) => {
          setSearchQuery(event.target.value);
          setCurrentPage(1);
        }}
        sortValue={sortKey}
        onSortChange={(event) => {
          setSortKey(event.target.value);
          setCurrentPage(1);
        }}
        metaText={`${apiCount}件中 ${visibleRows.length}件を表示${
          totalPages > 1 ? ` / ${safeCurrentPage} / ${totalPages}ページ` : ''
        }`}
        searchPlaceholder="主催者名 / 種別 / 会員 / メールアドレス / 代表者 / 稼働"
        sortOptions={organizerSortOptions}
      />
      <AdminRelatedLinks links={content.relatedLinks} preservedDomainSearch={preservedDomainSearch} />

      <div className="yowan-admin-resource-table-wrap c-surface-content">
        <table className="yowan-admin-resource-table">
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col" style={organizerColumnStyle}>主催者</th>
              <th scope="col" style={typeColumnStyle}>種別</th>
              <th scope="col" style={memberColumnStyle}>紐づく会員</th>
              <th scope="col">連絡先</th>
              <th scope="col" style={statusColumnStyle}>稼働</th>
              <th scope="col" style={statusColumnStyle}>公開</th>
              <th scope="col" style={updatedAtColumnStyle}>更新日</th>
              <th scope="col" style={actionColumnStyle}>操作</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, index) => {
              const activeUi = statusToUi(row.isActive);
              const publicUi = publicStatusToUi(row.publicProfileEnabled);

              return (
                <tr key={row.id}>
                  <td>{startIndex + index + 1}</td>
                  <td style={organizerColumnStyle}>
                    <p className="yowan-admin-resource-table__title">{row.organizerName}</p>
                    <p className="yowan-admin-resource-table__subline">{row.representativeName || '-'}</p>
                  </td>
                  <td style={typeColumnStyle}>{row.organizerTypeLabel}</td>
                  <td style={memberColumnStyle}>
                    <p className="yowan-admin-resource-table__title">{row.memberName || '未紐づき'}</p>
                    <p className="yowan-admin-resource-table__subline">{row.memberEmail || '外部主催者 / 団体'}</p>
                  </td>
                  <td>
                    <p className="yowan-admin-resource-table__title">{row.contactEmail || '-'}</p>
                    <p className="yowan-admin-resource-table__subline">{row.contactPhone || '-'}</p>
                  </td>
                  <td style={statusColumnStyle}>
                    <span className={`yowan-admin-resource-table__status yowan-admin-resource-table__status--${activeUi.tone}`}>
                      {activeUi.label}
                    </span>
                  </td>
                  <td style={statusColumnStyle}>
                    <span className={`yowan-admin-resource-table__status yowan-admin-resource-table__status--${publicUi.tone}`}>
                      {publicUi.label}
                    </span>
                  </td>
                  <td style={updatedAtColumnStyle}>{row.updatedAt || '-'}</td>
                  <td style={actionColumnStyle}>
                    <div className="yowan-admin-resource-table__actions">
                      <Link to={appendYowanPreviewQueryIfNeeded(row.editHref, preservedDomainSearch)}>編集</Link>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!isLoading && visibleRows.length === 0 ? (
              <tr>
                <td colSpan={9}>
                  {loadError || '表示できる主催者がありません。'}
                </td>
              </tr>
            ) : null}
            {isLoading ? (
              <tr>
                <td colSpan={9}>
                  <LoadingSpinner size="sm" />
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <AdminListPagination
        currentPage={safeCurrentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </AdminBlogShell>
  );
};

export default AdminOrganizersListPage;
